import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Briefcase, Loader2, MapPin, Filter } from "lucide-react";
import { companion } from "@/context/CompanionAuthContext";
import { useCompanionAuth } from "@/context/CompanionAuthContext";
import { STATUS_LABELS, STATUS_COLORS, formatDate } from "./utils";

const FILTERS = [
  { id: "all",     label: "All" },
  { id: "mine",    label: "Mine" },
  { id: "scheduled",   label: "Scheduled" },
  { id: "in_progress", label: "In Progress" },
  { id: "completed",   label: "Completed" },
];

const CompanionJobs = () => {
  const { user } = useCompanionAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState(user?.role === "crew" ? "mine" : "all");

  const load = async (f = filter) => {
    setLoading(true);
    const params = new URLSearchParams();
    if (f === "mine") params.set("mine", "true");
    else if (f !== "all") params.set("status", f);
    try {
      const { data } = await companion.get(`/jobs?${params.toString()}`);
      setItems(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(filter); /* eslint-disable-next-line */ }, [filter]);

  const grouped = useMemo(() => {
    const m = new Map();
    for (const j of items) {
      const k = j.scheduled_date || "Unscheduled";
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(j);
    }
    return Array.from(m.entries());
  }, [items]);

  return (
    <div className="px-4 py-5" data-testid="companion-jobs-page">
      <header className="mb-4">
        <p className="text-xs uppercase tracking-[0.22em] text-crest-gold font-medium">Jobs</p>
        <h1 className="mt-1 font-display text-2xl font-bold text-crest-navy">Schedule</h1>
      </header>

      <div className="flex items-center gap-1.5 overflow-x-auto pb-2 -mx-1 px-1" data-testid="jobs-filter-row">
        <Filter className="h-4 w-4 text-crest-navy/60 shrink-0" />
        {FILTERS.map((f) => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            className={`whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
              filter === f.id ? "bg-crest-green text-white" : "bg-white border border-border text-crest-navy hover:border-crest-green"
            }`}
            data-testid={`jobs-filter-${f.id}`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="grid place-items-center py-20"><Loader2 className="h-7 w-7 animate-spin text-crest-green" /></div>
      ) : items.length === 0 ? (
        <div className="mt-6 rounded-lg border border-dashed border-border bg-white p-8 text-center text-sm text-muted-foreground">
          No jobs match this filter.
        </div>
      ) : (
        <div className="mt-4 space-y-6">
          {grouped.map(([day, list]) => (
            <section key={day}>
              <h2 className="font-display text-sm font-semibold text-crest-navy/70 uppercase tracking-[0.14em] mb-2">{formatDate(day, "Unscheduled")}</h2>
              <ul className="space-y-2.5">
                {list.map((j) => (
                  <li key={j.id}>
                    <Link to={`/companion/jobs/${j.id}`} className="flex items-start gap-3 rounded-lg border border-border bg-white p-4 hover:border-crest-green transition-colors"
                          data-testid={`job-row-${j.id}`}>
                      <div className="h-10 w-10 grid place-items-center rounded-md bg-crest-green-100 text-crest-green shrink-0">
                        <Briefcase className="h-5 w-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-display font-semibold text-crest-navy truncate">{j.customer_name}</div>
                        <div className="flex items-center gap-1 text-xs text-muted-foreground mt-0.5 truncate">
                          <MapPin className="h-3.5 w-3.5 shrink-0" /> {j.address}{j.postcode ? `, ${j.postcode}` : ""}
                        </div>
                        <div className="mt-2 flex items-center gap-2">
                          <span className={`text-[11px] px-2 py-0.5 rounded ${STATUS_COLORS[j.status]}`}>{STATUS_LABELS[j.status]}</span>
                          {j.scheduled_time_start && <span className="text-[11px] text-muted-foreground">{j.scheduled_time_start}</span>}
                          {j.service_type && <span className="text-[11px] text-crest-gold">{j.service_type}</span>}
                        </div>
                      </div>
                      <ArrowRight className="h-5 w-5 text-crest-navy/40 mt-1" />
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}
    </div>
  );
};

export default CompanionJobs;
